<template>
    <section>
        <div class="blocks-container">
            <BlocksRichText class="rich-text-block" />
            <BlocksNeighborhoodsCarousel :neighborhoods="neighborhoods" v-if="neighborhoods && neighborhoods.length > 0" />
            <BlocksSitesCarousel type="large" title="2026 Sites" :sites="homepageSites" v-if="homepageSites && homepageSites.length > 0" />
            <BlocksToursCarousel :items="tours" v-if="tours && tours.length > 0" />
            <BlocksLiveEvents :items="events" abridged v-if="events && events.length > 0" />
            <BlocksMoreFromCarousel />
            <div class="hd-container">
                <BlocksSponsorsList />
            </div>
        </div>
    </section>
</template>

<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useEventsStore } from '@/stores/events';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useToursStore } from '@/stores/tours';

    // Props
    const { showBanner } = defineProps({
        showBanner: {
            type: Boolean,
            default: false,
        },
    });

    // Tours Store
    const { toursData } = storeToRefs(useToursStore());
    const tours = computed(() => toursData.value?.records);

    // Sites Store
    const { sitesData } = storeToRefs(useSitesStore());
    const sites = computed(() => sitesData.value);

    const homepageSites = computed(() => {
        return sites.value.filter((site) => site?.fields['Highlight on Homepage'] && site?.fields['Highlight on Homepage'] === true);
    });

    // Events Store
    const { eventsData } = storeToRefs(useEventsStore());
    const events = computed(() => eventsData.value?.records);

    // Neighborhoods Store
    const { neighborhoodsData } = storeToRefs(useNeighborhoodsStore());
    const neighborhoods = computed(() => neighborhoodsData.value);
</script>

<style lang="scss">
    .home-banner .container {
        @media (min-width: $tablet) {
            width: 80%;
            max-width: $hd;
            margin: auto;
        }
    }

    .blocks-container {
        @media (min-width: $tablet) {
            width: 100%;
            max-width: $hd;
            margin: auto;

            .rich-text-block {
                display: block;
            }

            // No `.component-header { padding-right: 0 }` here — see the note
            // in app/pages/ohc-home/index.vue. The block is capped at $hd and
            // centred, but the cap only yields a margin above $hd, so between
            // $tablet and 1150 this left the View All buttons flush to the
            // right edge while their headings sat at 16. hd-container on the
            // header already drops the gutter at the right width.

            .view-all {
                font-size: 16px;
                width: auto;
                padding: 16px 15px 14px;
                font-size: 18px;
                font-weight: 700;
                font-family: $title;
                text-align: center;
                text-transform: uppercase;
                display: inline-block;
                border: 1px solid;
                border-radius: 3.333em;
                padding: 1em 2em;
                transition: 0.25s all;

                &:hover {
                    background-color: $black;
                    color: $white;
                }
            }

            .neighborhoods-carousel {
                padding-top: 60px;

                .carousel-component .carousel-inner .carousel-item {
                    flex: 0 0 27%;
                    padding: 0 4px;

                    @media (min-width: $tablet) {
                        padding: 0;
                    }
                }
            }
            .carousel-item {
                border: 0px !important;
            }

            .card-site.large .image-wrapper {
                padding-top: 60%;
                width: 100%;
            }

            .carousel-component .carousel-inner .carousel-item.is-full-width {
                padding: 0 8px;
                
                @media (min-width: $tablet) {
                    padding: 0;
                }
            }

            // Same treatment as like-a-local-carousel.vue and the matching block
            // in app/pages/ohc-home/index.vue: over the cards by default,
            // outside the content only once there is margin to hang into.
            // Below $hd + room for both arrows there is none, and they ran off
            // the right of the window.
            .carousel-component .arrow {
                top: 50%;
                transform: translateY(-50%);
                left: 8px;
                box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
            }

            .carousel-component .arrow:last-of-type {
                right: 8px;
                left: unset;
            }

            @media (min-width: $hd + 240px) {
                .carousel-component .arrow {
                    left: -50px;
                    box-shadow: none;
                }

                .carousel-component .arrow:last-of-type {
                    right: -60px;
                    left: unset;
                }
            }

            .live-events .container {
                grid-template-columns: repeat(3, 1fr);
            }

            .sponsors-list h2 {
                font-size: 24px;
                text-align: center;
            }

            .sponsors-list {
                padding: 80px 0 150px;

                .container {
                    padding: 0px;
                }
            }

            .sites-carousel .carousel-component .carousel-inner .carousel-item.is-full-width {
                flex: 0 0 35.4%;
            }

            .tours-carousel .carousel-component .carousel-inner .carousel-item.is-full-width {
                flex: 0 0 35.4%;
            }

            .live-events .event {
                margin: 0px;
            }

            .live-events ul {
                gap: 24px;
                padding: 0;
            }

            .more-from-carousel .carousel-item .text-over-image {
                padding-top: 50%;
                pointer-events: auto;

                h3 {
                    font-size: 22px;
                }
            }
        }
    }
</style>
